import { ChartDataset } from 'chart.js'
import { rgb } from 'd3-color'

import { GraphData } from 'src/api/gen'
import { useProviders } from 'src/layout/hooks'
import { DEVICE_STATUS_COLOR } from 'src/theme'

interface IChartJsDatasetColors {
  backgroundColor: ChartDataset['backgroundColor']
  borderColor: ChartDataset['borderColor']
  hoverBackgroundColor: ChartDataset['hoverBackgroundColor']
  hoverBorderColor: ChartDataset['hoverBorderColor']
}

interface IChartJsDatasetWidths {
  borderWidth: ChartDataset['borderWidth']
  hoverBorderWidth: ChartDataset['hoverBorderWidth']
}

export type IChartJsDatasetStyles = IChartJsDatasetColors & IChartJsDatasetWidths

export function getDatasetColors(colorSpecifier: string): IChartJsDatasetColors {
  const color = rgb(colorSpecifier).darker(0.5)
  const backgroundColor = color.copy({ opacity: 0.2 })
  const borderColor = color.copy({ opacity: 1 })
  const hoverColor = backgroundColor.darker(0.8)
  const hoverBorderColor = borderColor.darker(0.8)

  return {
    backgroundColor: backgroundColor.toString(),
    borderColor: borderColor.toString(),
    hoverBackgroundColor: hoverColor.toString(),
    hoverBorderColor: hoverBorderColor.toString(),
  }
}

export function getDatasetBorderWidth(borderWidth = 1, hoverBorderWidth = 3): IChartJsDatasetWidths {
  return {
    borderWidth,
    hoverBorderWidth,
  }
}

export enum DatasetCategory {
  provider = 'provider',
  status = 'status',
}

export function getColorMapping(category: DatasetCategory | undefined, providerColor: Record<string, string>) {
  switch (category) {
    case DatasetCategory.provider:
      return providerColor
    case DatasetCategory.status:
      return DEVICE_STATUS_COLOR
    default:
      return undefined
  }
}

/**
 * Creates a mapping <providers | device statuses, dataset style properties>.
 *
 * Whether the mapping keys are providers or statuses is decided by the 'category' parameter.
 * If 'category' is undefined, the mapping will be empty (i.e. {}).
 *
 * When an undefined property is accessed, the mapping, through its Proxy handler, will return
 * a dataset style object according to the 'defautColors' array parameter. The exact chosen
 * color is decied via a counter variable 'index', which is incremented every time an undefined
 * property is accessed.
 */
export function useDatasetStyleProps(
  category: DatasetCategory | undefined,
  defaultColors: string[]
): Record<string, IChartJsDatasetStyles> {
  const { colors: providerColor } = useProviders()

  const undefinedPropertyHandler = {
    index: 0,
    get: function (target: Record<string, IChartJsDatasetStyles>, name: string) {
      if (name in target) {
        return target[name]
      }
      const colorSpecifier = defaultColors[this.index % defaultColors.length]
      this.index += 1
      const result = { ...getDatasetColors(colorSpecifier), ...getDatasetBorderWidth() }
      // memoize the result
      target[name] = result

      return result
    },
  }

  const datasetStylesMapping: Record<string, IChartJsDatasetStyles> = new Proxy({}, undefinedPropertyHandler)

  const colorMapping: Record<string, string> | undefined = getColorMapping(category, providerColor)

  if (colorMapping) {
    for (const [key, colorSpecifier] of Object.entries(colorMapping)) {
      const datasetStyles = { ...getDatasetColors(colorSpecifier), ...getDatasetBorderWidth() }
      datasetStylesMapping[key] = datasetStyles
    }
  }

  return datasetStylesMapping
}

export interface IComparisonBucket {
  average: number
  compareBy: {
    buckets: {
      doc_count: number
      key: string
    }[]
  }
  key: string
}

export function convertChartJsDataToComparisonFormat(graphData: GraphData): IComparisonBucket[] {
  if (Array.isArray(graphData)) {
    // empty data from API
    return []
  }
  const { labels, datasets: yAxisDatasets } = graphData
  const numberOfCategories = yAxisDatasets.length

  const comparisonBuckets = []
  for (const [idx, date] of labels.entries()) {
    let total = 0
    const bucketsCorrespondingToLabel = []
    for (const dataset of yAxisDatasets) {
      const yAxisDatum = dataset.data[idx]
      bucketsCorrespondingToLabel.push({ key: dataset.label, doc_count: yAxisDatum })
      total += yAxisDatum
    }

    comparisonBuckets.push({
      average: total ? Math.round(total / numberOfCategories) : 0,
      compareBy: { buckets: bucketsCorrespondingToLabel },
      key: date,
    })
  }
  return comparisonBuckets
}

export interface IComparisonPercentBucket {
  compareBy: {
    buckets: {
      doc_count: number
      key: string
    }[]
  }
  key: string
}

export function convertChartJsDataToComparisonPercentFormat(graphData: GraphData): IComparisonPercentBucket[] {
  const { labels, datasets: yAxisDatasets } = graphData

  const comparisonBuckets = []
  for (const [idx, date] of labels.entries()) {
    let total_doc_count = 0
    const bucketsCorrespondingToLabel = []
    for (const dataset of yAxisDatasets) {
      total_doc_count += dataset.data[idx]
    }
    for (const dataset of yAxisDatasets) {
      const doc_count = total_doc_count ? Math.round((dataset.data[idx] / total_doc_count) * 100) : 0
      bucketsCorrespondingToLabel.push({ key: dataset.label, doc_count })
    }

    comparisonBuckets.push({
      compareBy: { buckets: bucketsCorrespondingToLabel },
      key: date,
    })
  }
  return comparisonBuckets
}

export interface IHistogramBucket {
  doc_count: number
  key: string
  stackedBy: {
    buckets: {
      doc_count: number
      key: string
    }[]
  }
}

export function convertChartJsDataToHistogramFormat(graphData: GraphData, toPercentage?: boolean): IHistogramBucket[] {
  const { labels, datasets: yAxisDatasets } = graphData

  const histogramBuckets = []
  for (const [idx, date] of labels.entries()) {
    let total_doc_count = 0
    const bucketsCorrespondingToLabel = []
    for (const dataset of yAxisDatasets) {
      total_doc_count += dataset.data[idx]
    }
    for (const dataset of yAxisDatasets) {
      let doc_count = dataset.data[idx]
      if (toPercentage) {
        doc_count = total_doc_count ? Math.round((doc_count / total_doc_count) * 100) : 0
      }
      bucketsCorrespondingToLabel.push({ key: dataset.label, doc_count })
    }

    histogramBuckets.push({
      stackedBy: { buckets: bucketsCorrespondingToLabel },
      key: date,
      doc_count: toPercentage ? 100 : total_doc_count,
    })
  }
  return histogramBuckets
}
